import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Home,
  Youtube,
  Sparkles,
  Heart,
  Shield,
  TrendingUp,
  FileText,
  ChevronDown,
  Flame,
  BookOpen,
  Bell,
  Layout,
  Mic,
  Captions,
  Images,
  Monitor,
  // Video, // DESCONTINUADO em 2026-05-10: usado apenas pelo item "DarkPlanner Tools" (substituído pelo app desktop oficial). Rota /extension mantida ativa por compatibilidade.
  AlertTriangle,
  X,
  GraduationCap,
  // ShieldCheck, // Score de Confiança OCULTO do menu em 2026-07-21 (pedido do owner). Rota /academia/score-de-confianca segue ativa.
  MessageCircle,
  Plus,
  Zap,
  Link2,
  Users,
  Network,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import DarkTooltip from './ui/DarkTooltip';
import { usePrivacy } from '../contexts/PrivacyContext';
import { useSidebar } from '../contexts/SidebarContext';
import { useAdmin } from '../contexts/AdminContext';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Site de vendas (landing, deploy separado em darkplanner.com.br). O app roda em app.darkplanner.com.br.
const SALES_PRECOS_URL = 'https://darkplanner.com.br/precos';

// ── Helper: hex color → rgba with opacity ──
const colorWithOpacity = (hex, opacity) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

// ── Sub-component: Collapsible Group ──
const SidebarGroup = ({ group, isExpanded, children, location }) => {
  const [isOpen, setIsOpen] = useState(() => {
    const saved = localStorage.getItem(`sidebar-group-${group.id}`);
    return saved !== null ? JSON.parse(saved) : true;
  });

  // Check if any child item is active
  const hasActiveChild = group.items.some((item) => {
    if (item.path.includes('?')) {
      const [pathname] = item.path.split('?');
      return location.pathname === pathname;
    }
    return location.pathname === item.path;
  });

  const toggle = () => {
    const next = !isOpen;
    setIsOpen(next);
    localStorage.setItem(`sidebar-group-${group.id}`, JSON.stringify(next));
  };

  if (!isExpanded) {
    // Recolhido: mantém a ALTURA da linha do título (nome invisível) para os
    // ícones preservarem exatamente o mesmo espaçamento vertical do expandido.
    return (
      <div style={{ marginBottom: '2px' }}>
        <div style={{
          padding: '6px 14px',
          fontSize: '10px',
          fontWeight: 600,
          lineHeight: '12px',
          opacity: 0,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          pointerEvents: 'none',
          userSelect: 'none',
          fontFamily: 'var(--dp-font)',
        }}>
          {group.label}
        </div>
        <div style={{ paddingLeft: '2px' }}>{children}</div>
      </div>
    );
  }

  return (
    <div style={{ marginBottom: '2px' }}>
      {/* Group header — clickable to toggle */}
      <button
        onClick={toggle}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '6px 14px',
          fontSize: '10px',
          fontWeight: 600,
          color: hasActiveChild ? group.color : 'var(--dp-text-tertiary)',
          textTransform: 'uppercase',
          letterSpacing: '1.2px',
          fontFamily: 'var(--dp-font)',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          transition: 'color 0.2s',
        }}
        onMouseEnter={(e) => {
          if (!hasActiveChild) e.currentTarget.style.color = 'var(--dp-text-secondary)';
        }}
        onMouseLeave={(e) => {
          if (!hasActiveChild) e.currentTarget.style.color = 'var(--dp-text-tertiary)';
        }}
      >
        <span>{group.label}</span>
        <ChevronDown
          size={12}
          style={{
            transition: 'transform 0.2s ease',
            transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
            opacity: 0.6,
          }}
        />
      </button>

      {/* Group content */}
      {isOpen && (
        <div style={{ paddingLeft: '2px' }}>
          {children}
        </div>
      )}
    </div>
  );
};

// ── Main Sidebar Component ──
// onLogout não é mais usado aqui (logout migrou pro menu do avatar no Header);
// as páginas continuam passando a prop — é simplesmente ignorada.
const Sidebar = ({ isSuperAdmin: isSuperAdminProp }) => {
  const { isPrivate } = usePrivacy();
  const { isCollapsed, toggleSidebar, isMobile, isMobileOpen, toggleMobileMenu, closeMobileMenu } = useSidebar();
  const { isSuperAdmin: isSuperAdminContext, isModerator, subscriptionData } = useAdmin();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const isSuperAdmin = isSuperAdminContext || isSuperAdminProp || false;

  // API docs access gate — same logic as MyPlanPage / ApiDocsPage
  const planType = subscriptionData?.plan_type?.toLowerCase() || '';
  const hasApiAccess = ['pro', 'plus', 'master'].includes(planType) || isSuperAdmin;

  // Channels: read from sessionStorage (written by CanalManagerNew on fetch)
  // Avoids API calls in Sidebar — no re-renders, no loops
  const [channels, setChannels] = useState(() => {
    try {
      const cached = sessionStorage.getItem('sidebar-channels');
      return cached ? JSON.parse(cached) : [];
    } catch { return []; }
  });

  // Shared channels: read from sessionStorage
  const [sharedChannels, setSharedChannels] = useState(() => {
    try {
      const cached = sessionStorage.getItem('sidebar-shared-channels');
      return cached ? JSON.parse(cached) : [];
    } catch { return []; }
  });

  // Refresh channels from cache via custom event (fired by pages that fetch channels)
  useEffect(() => {
    const handleChannelsUpdated = () => {
      try {
        const cached = sessionStorage.getItem('sidebar-channels');
        if (cached) setChannels(JSON.parse(cached));
        const cachedShared = sessionStorage.getItem('sidebar-shared-channels');
        if (cachedShared) setSharedChannels(JSON.parse(cachedShared));
      } catch { /* ignore */ }
    };
    window.addEventListener('sidebar-channels-updated', handleChannelsUpdated);
    return () => window.removeEventListener('sidebar-channels-updated', handleChannelsUpdated);
  }, []);

  // ── Sidebar Groups Config ──
  const sidebarGroups = [
    {
      id: 'visao-geral',
      label: t('sidebar.group_overview'),
      color: '#3B82F6',
      items: [
        { path: '/', icon: Home, label: t('nav.dashboard') },
      ],
    },
    {
      id: 'meus-canais',
      label: t('sidebar.group_channels'),
      color: '#10B981',
      items: [
        { path: '/canais', icon: Youtube, label: t('sidebar.channels_management') },
      ],
      hasDynamicChannels: true,
    },
    {
      id: 'biblioteca',
      label: t('sidebar.group_library'),
      color: '#8B5CF6',
      items: [
        { path: '/prompts', icon: Sparkles, label: t('nav.prompts') },
        { path: '/referencias', icon: Heart, label: t('nav.references') },
        { path: '/biblioteca-midia', icon: Images, label: t('nav.media_library') },
      ],
    },
    {
      id: 'ferramentas',
      label: t('sidebar.group_tools'),
      color: '#F59E0B',
      items: [
        { path: '/audio-generator', icon: Mic, label: t('nav.audio_generator') },
        { path: '/transcricao', icon: Captions, label: t('nav.transcricao') },
        // TODO: remover badge NOVO em ~10/jul/2026 (60 dias)
        // Ícone Monitor = mesmo da página DarkPlanner App (PageHeader).
        { path: '/darkplanner-app', icon: Monitor, label: t('sidebar.download_app'), badge: 'NOVO' },
        { path: '/text-tools', icon: FileText, label: t('nav.text_tools') },
        { path: '/quadro-ideias', icon: Layout, label: t('nav.idea_board') },
        // DESCONTINUADO em 2026-05-10: substituído pelo app desktop oficial. Rota mantida ativa por compatibilidade, apenas escondida do menu.
        // { path: '/extension', icon: Video, label: t('nav.tools') },
      ],
    },
    {
      id: 'tendencias',
      label: t('sidebar.group_trends'),
      color: '#EAB308',
      items: [
        { path: '/nichos-virais', icon: Flame, label: t('viralNiches.title') },
        { path: '/dobra-nicho', icon: Network, label: t('dobraNicho.title') },
        { path: '/videos-virais', icon: TrendingUp, label: t('nav.viral_videos') },
        { path: '/canais-removidos', icon: AlertTriangle, label: t('nav.removed_channels') },
      ],
    },
    {
      id: 'academia',
      label: t('sidebar.group_academy'),
      color: '#06B6D4',
      items: [
        // Item único (2026-06): a navegação interna (Início/Treinamento/
        // Cursos/Recursos) vive nas pills da própria página, estilo Netflix.
        { path: '/academia', icon: GraduationCap, label: t('academia.title') },
        // Score de Confiança OCULTO do menu em 2026-07-21 (pedido do owner).
        // A página existe e a rota segue ativa: /academia/score-de-confianca.
        // { path: '/academia/score-de-confianca', icon: ShieldCheck, label: t('trustScore.nav_label') },
      ],
    },
    {
      id: 'suporte',
      label: t('sidebar.group_support'),
      color: '#06B6D4',
      items: [
        { path: '/comunidade', icon: MessageCircle, label: t('sidebar.community_support') },
        ...(hasApiAccess ? [{ path: '/api-docs', icon: BookOpen, label: 'Documentação API' }] : []),
      ],
    },
  ];

  // Add SuperAdmin group (moderador também acessa o painel — o backend limita
  // o que ele pode fazer lá dentro; Notificações segue só super admin)
  if (isSuperAdmin || isModerator) {
    sidebarGroups.push({
      id: 'admin',
      label: t('nav.administracao'),
      color: '#EF4444',
      items: [
        { path: '/super-admin', icon: Shield, label: t('nav.super_admin') },
        ...(isSuperAdmin ? [{ path: '/notifications', icon: Bell, label: t('nav.notifications') }] : []),
      ],
    });
  }

  // ── isActive check — handles query params for academia ──
  const isActive = (path) => {
    if (path.includes('?')) {
      const [pathname, queryString] = path.split('?');
      if (location.pathname !== pathname) return false;
      // Check query param match
      const params = new URLSearchParams(queryString);
      const searchParams = new URLSearchParams(location.search);
      for (const [key, value] of params) {
        if (searchParams.get(key) === value) return true;
      }
      // /academia sem query agora abre na aba Início (nenhum sub-item do
      // sidebar corresponde ao default) — sem highlight forçado.
      return false;
    }
    return location.pathname === path;
  };

  // (Selo do plano migrou pro Header/HeaderUserMenu — helpers de exibição vivem lá.)

  // Free (conta nova OU cancelou/reembolsou e já saiu da vigência) → mostra Upgrade p/ o site de vendas.
  // Premium e super admin NÃO veem.
  const isFreeUser = !!subscriptionData && !subscriptionData.is_super_admin && subscriptionData.estado_efetivo !== 'PREMIUM';

  // ── Display mode ──
  const showExpanded = isMobile ? true : !isCollapsed;
  const sidebarDisplayWidth = isMobile ? '260px' : (isCollapsed ? '68px' : '232px');

  const handleNavClick = () => {
    if (isMobile) closeMobileMenu();
  };

  // ── Handle navigation — uses custom events for /canais when already there ──
  const handleItemClick = (e, item) => {
    // Special handling for /canais route: dispatch custom event when already on /canais
    if (item.path === '/canais' && location.pathname === '/canais') {
      e.preventDefault();
      // Clear selected channel (go back to grid)
      localStorage.removeItem('selectedCanal');
      window.dispatchEvent(new CustomEvent('sidebar-navigate-canal', { detail: { action: 'grid' } }));
      handleNavClick();
      return;
    }
    if (item.path === '/canais') {
      // Vindo de outra página: "Gestão de Canais" abre a GRADE, não o último
      // canal visto — quem abre canal específico são os itens de canal abaixo.
      localStorage.removeItem('selectedCanal');
    }
    handleNavClick();
  };

  // ── Render a single nav item ──
  const renderNavItem = (item, groupColor) => {
    const Icon = item.icon;
    const active = isActive(item.path);

    return (
      <Link
        key={item.path}
        to={item.path}
        onClick={(e) => handleItemClick(e, item)}
        style={{
          display: 'flex',
          alignItems: 'center',
          // Layout CONSTANTE nos dois estados: o ícone nunca muda de posição
          // ao recolher/expandir — só o nome faz fade e é clipado pela largura.
          justifyContent: 'flex-start',
          gap: '10px',
          padding: '6px 14px',
          borderRadius: '6px',
          color: active ? 'var(--dp-text-primary)' : 'var(--dp-text-secondary)',
          cursor: 'pointer',
          transition: 'background 0.2s ease, color 0.2s ease',
          fontSize: '13px',
          fontWeight: active ? 500 : 400,
          textDecoration: 'none',
          position: 'relative',
          background: active ? colorWithOpacity(groupColor, 0.15) : 'transparent',
          fontFamily: 'var(--dp-font)',
          marginBottom: '1px',
          overflow: 'hidden',
        }}
        onMouseEnter={(e) => {
          if (!active) {
            e.currentTarget.style.background = 'var(--dp-bg-card)';
            e.currentTarget.style.color = 'var(--dp-text-primary)';
          }
        }}
        onMouseLeave={(e) => {
          if (!active) {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = 'var(--dp-text-secondary)';
          }
        }}
        title={!showExpanded ? item.label : ''}
      >
        {/* Active bar */}
        {active && (
          <div style={{
            position: 'absolute',
            left: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            width: '3px',
            height: '20px',
            background: groupColor,
            borderRadius: '0 3px 3px 0',
          }} />
        )}
        <Icon size={18} style={{ flexShrink: 0, opacity: active ? 1 : 0.7 }} />
        {/* Nome SEMPRE renderizado — fade suave ao recolher (não quebra linha,
            é clipado pelo overflow do item). */}
        <span style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            flex: 1,
            minWidth: 0,
            opacity: showExpanded ? 1 : 0,
            transition: 'opacity 0.22s ease',
            pointerEvents: showExpanded ? 'auto' : 'none',
          }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {item.label}
            </span>
            {item.badge && (
              <span style={{
                fontSize: '8px',
                fontWeight: 700,
                color: 'var(--dp-green-500)',
                background: 'rgba(16, 185, 129, 0.15)',
                border: '1px solid var(--dp-green-500)',
                padding: '0px 4px',
                borderRadius: '4px',
                letterSpacing: '0.3px',
                textTransform: 'uppercase',
                lineHeight: 1.3,
                flexShrink: 0,
              }}>
                {item.badge}
              </span>
            )}
          </span>
      </Link>
    );
  };

  // ── Render dynamic channels list ──
  const renderDynamicChannels = (groupColor) => {
    if (!showExpanded || channels.length === 0) return null;

    const sortedChannels = [...channels].filter(c => !c.is_archived).sort((a, b) => {
      const nameA = (a.nome || '').toLowerCase();
      const nameB = (b.nome || '').toLowerCase();
      const numA = nameA.match(/^\d+/);
      const numB = nameB.match(/^\d+/);
      if (numA && numB) {
        const diff = parseInt(numA[0]) - parseInt(numB[0]);
        if (diff !== 0) return diff;
      }
      return nameA.localeCompare(nameB, 'pt-BR');
    });
    const visibleChannels = sortedChannels.slice(0, 5);
    const hasMore = sortedChannels.length > 5;

    return (
      <>
        {/* Separator */}
        <div style={{
          height: '1px',
          background: 'var(--dp-border-subtle, rgba(255,255,255,0.06))',
          margin: '4px 14px',
        }} />

        {/* Channel list */}
        {visibleChannels.map((channel) => {
          const savedCanal = localStorage.getItem('selectedCanal');
          const active = location.pathname === '/canais' && savedCanal === channel.id;

          return (
            <div
              key={channel.id}
              onClick={() => {
                localStorage.setItem('selectedCanal', channel.id);
                if (location.pathname === '/canais') {
                  // Already on /canais — dispatch custom event (no navigation needed)
                  window.dispatchEvent(new CustomEvent('sidebar-navigate-canal', { detail: { action: 'select', canalId: channel.id } }));
                } else {
                  // Not on /canais — navigate there, CanalManagerNew will read from localStorage on mount
                  navigate('/canais');
                }
                handleNavClick();
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '4px 14px 4px 18px',
                borderRadius: '6px',
                color: active ? 'var(--dp-text-primary)' : 'var(--dp-text-tertiary)',
                fontSize: '12px',
                fontWeight: active ? 500 : 400,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                background: active ? colorWithOpacity(groupColor, 0.12) : 'transparent',
                fontFamily: 'var(--dp-font)',
              }}
              onMouseEnter={(e) => {
                if (!active) {
                  e.currentTarget.style.color = 'var(--dp-text-secondary)';
                  e.currentTarget.style.background = 'var(--dp-bg-card)';
                }
              }}
              onMouseLeave={(e) => {
                if (!active) {
                  e.currentTarget.style.color = 'var(--dp-text-tertiary)';
                  e.currentTarget.style.background = 'transparent';
                }
              }}
            >
              {/* Channel avatar */}
              {!isPrivate && channel.foto_url ? (
                <img
                  src={channel.foto_url}
                  alt=""
                  style={{
                    width: '18px',
                    height: '18px',
                    borderRadius: '50%',
                    objectFit: 'cover',
                    flexShrink: 0,
                  }}
                />
              ) : (
                // Ícone YouTube: vermelho = canal vinculado, cinza = não vinculado
                <Youtube
                  size={14}
                  style={{
                    flexShrink: 0,
                    color: channel.is_youtube_linked ? '#FF0000' : 'currentColor',
                    opacity: channel.is_youtube_linked ? 1 : 0.5,
                  }}
                />
              )}
              <span style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
                {isPrivate ? '••••••••' : (channel.nome || channel.nome_canal || 'Canal')}
              </span>
            </div>
          );
        })}

        {/* "Ver todos →" link */}
        {hasMore && (
          <div
            onClick={() => {
              if (location.pathname === '/canais') {
                localStorage.removeItem('selectedCanal');
                window.dispatchEvent(new CustomEvent('sidebar-navigate-canal', { detail: { action: 'grid' } }));
              } else {
                localStorage.removeItem('selectedCanal');
                navigate('/canais');
              }
              handleNavClick();
            }}
            style={{
              display: 'block',
              padding: '3px 14px 3px 18px',
              fontSize: '11px',
              color: groupColor,
              textDecoration: 'none',
              fontWeight: 500,
              fontFamily: 'var(--dp-font)',
              transition: 'opacity 0.2s',
              cursor: 'pointer',
            }}
            onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
            onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
          >
            {t('sidebar.view_all_channels')}
          </div>
        )}

        {/* "+ Novo Canal" button */}
        <button
          onClick={() => {
            if (location.pathname === '/canais') {
              window.dispatchEvent(new CustomEvent('sidebar-navigate-canal', { detail: { action: 'novo' } }));
            } else {
              // Navigate to /canais with query param for mount-time processing
              navigate('/canais?novo=true');
            }
            handleNavClick();
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '4px 14px 4px 18px',
            fontSize: '12px',
            color: groupColor,
            fontWeight: 500,
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            fontFamily: 'var(--dp-font)',
            transition: 'opacity 0.2s',
            width: '100%',
          }}
          onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
          onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
        >
          <Plus size={14} />
          {t('sidebar.new_channel')}
        </button>
      </>
    );
  };

  // ── Render shared channels section ──
  const renderSharedChannels = () => {
    const acceptedShared = sharedChannels.filter(c => c.comp_status !== 'pendente');
    if (!showExpanded || acceptedShared.length === 0) return null;

    const sharedColor = '#8B5CF6'; // Purple for shared

    return (
      <>
        {/* Separator + Label */}
        <div style={{
          height: '1px',
          background: 'var(--dp-border-subtle, rgba(255,255,255,0.06))',
          margin: '8px 14px 4px',
        }} />
        <div style={{
          padding: '4px 14px',
          fontSize: '10px',
          fontWeight: 700,
          color: 'var(--dp-text-tertiary)',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          fontFamily: 'var(--dp-font)',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
        }}>
          <Users size={10} />
          Compartilhados
        </div>

        {/* Shared channel list (only accepted, not pending) */}
        {sharedChannels.filter(c => !c.is_archived && c.comp_status !== 'pendente').slice(0, 5).map((channel) => {
          const savedCanal = localStorage.getItem('selectedCanal');
          const active = location.pathname === '/canais' && savedCanal === channel.id;

          return (
            <div
              key={channel.id}
              onClick={() => {
                localStorage.setItem('selectedCanal', channel.id);
                if (location.pathname === '/canais') {
                  window.dispatchEvent(new CustomEvent('sidebar-navigate-canal', { detail: { action: 'select', canalId: channel.id } }));
                } else {
                  navigate('/canais');
                }
                handleNavClick();
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '4px 14px 4px 18px',
                borderRadius: '6px',
                color: active ? 'var(--dp-text-primary)' : 'var(--dp-text-tertiary)',
                fontSize: '12px',
                fontWeight: active ? 500 : 400,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                background: active ? colorWithOpacity(sharedColor, 0.12) : 'transparent',
                fontFamily: 'var(--dp-font)',
              }}
              onMouseEnter={(e) => {
                if (!active) {
                  e.currentTarget.style.color = 'var(--dp-text-secondary)';
                  e.currentTarget.style.background = 'var(--dp-bg-card)';
                }
              }}
              onMouseLeave={(e) => {
                if (!active) {
                  e.currentTarget.style.color = 'var(--dp-text-tertiary)';
                  e.currentTarget.style.background = 'transparent';
                }
              }}
            >
              {/* Shared icon */}
              <Link2 size={14} style={{ flexShrink: 0, opacity: 0.6, color: sharedColor }} />
              <span style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
                {isPrivate ? '••••••••' : (channel.nome || channel.nome_canal || 'Canal')}
              </span>
            </div>
          );
        })}
      </>
    );
  };

  return (
    <>
      {/* ── Mobile backdrop overlay ── */}
      {isMobile && isMobileOpen && (
        <div
          onClick={closeMobileMenu}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
            zIndex: 199,
            transition: 'opacity 0.3s ease',
          }}
        />
      )}

      {/* ── Sidebar ── */}
      <aside style={{
        position: 'fixed',
        left: 0,
        top: 0,
        bottom: 0,
        width: sidebarDisplayWidth,
        background: 'var(--dp-bg-sidebar)',
        display: 'flex',
        flexDirection: 'column',
        zIndex: isMobile ? 200 : 100,
        // Largura anima com a MESMA curva do transform mobile — efeito suave;
        // nomes fazem fade (opacity) e são clipados, nunca quebram linha.
        transition: isMobile ? 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)' : 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        fontFamily: 'var(--dp-font)',
        transform: isMobile
          ? (isMobileOpen ? 'translateX(0)' : 'translateX(-100%)')
          : 'translateX(0)',
        boxShadow: isMobile && isMobileOpen ? '8px 0 32px rgba(0, 0, 0, 0.3)' : 'none',
      }}>

        {/* ═══════════ HEADER — Logo ═══════════
            Altura FIXA de 60px = mesma do Header do app, para logo, nome,
            breadcrumb, ícones e pill do avatar ficarem na MESMA linha (hero). */}
        <div style={{
          height: '60px',
          boxSizing: 'border-box',
          padding: '0 16px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          justifyContent: 'flex-start',
          position: 'relative',
          flexShrink: 0,
          overflow: 'hidden',
        }}>
          <div
            onClick={() => { navigate('/'); handleNavClick(); }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              cursor: 'pointer',
              transition: 'opacity 0.2s',
            }}
            onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
            onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
            title={t('sidebar.go_home')}
          >
            <img
              src="/logo-darkplanner.png"
              alt="Dark Planner"
              style={{
                width: '34px',
                height: '34px',
                borderRadius: '10px',
                boxShadow: '0 0 20px rgba(13, 174, 255, 0.3)',
                flexShrink: 0,
                objectFit: 'cover',
              }}
            />
            {/* Nome sempre renderizado — fade ao recolher (logo nunca se move) */}
            <span style={{
              fontWeight: 600,
              fontSize: '15px',
              letterSpacing: '-0.3px',
              color: 'var(--dp-text-primary)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              opacity: showExpanded ? 1 : 0,
              transition: 'opacity 0.22s ease',
            }}>Dark Planner</span>
          </div>
        </div>

        {/* Fechar (X) — só no overlay MOBILE. No desktop, o botão de painel
            vive no Header, antes do breadcrumb (HeaderUserMenu/hero). */}
        {isMobile && (
          <div style={{ display: 'flex', padding: '2px 8px 0 16px', flexShrink: 0 }}>
            <DarkTooltip content={t('sidebar.close_menu')} position="bottom">
              <button
                onClick={closeMobileMenu}
                aria-label={t('sidebar.close_menu')}
                style={{
                  width: '26px',
                  height: '26px',
                  borderRadius: '6px',
                  background: 'transparent',
                  border: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: 'var(--dp-text-secondary)',
                  transition: 'all 0.2s',
                  flexShrink: 0,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'var(--dp-bg-card)';
                  e.currentTarget.style.color = 'var(--dp-text-primary)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = 'var(--dp-text-secondary)';
                }}
              >
                <X size={15} />
              </button>
            </DarkTooltip>
          </div>
        )}

        {/* Botão flutuante de expandir REMOVIDO — abrir/fechar agora é feito
            pelo botão de painel no Header (antes do breadcrumb). */}

        {/* Selo do plano REMOVIDO — o plano agora aparece na pill do Header
            (HeaderUserMenu), com a data no tooltip. */}

        {/* ═══════════ UPGRADE (usuários free) ═══════════ */}
        {/* Conta nova ou cancelada/reembolsada → leva ao site de vendas /precos pra comprar o plano. */}
        {isFreeUser && (
          <div style={{ margin: showExpanded ? '6px 12px 4px' : '8px 12px 4px' }}>
            <a
              href={SALES_PRECOS_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleNavClick}
              title={!showExpanded ? t('sidebar.upgrade') : ''}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '7px',
                padding: showExpanded ? '9px 12px' : '9px 0',
                borderRadius: '8px',
                background: 'linear-gradient(135deg, var(--dp-blue-500, #0DAEFF), #3B82F6)',
                color: '#fff',
                textDecoration: 'none',
                fontSize: '13px',
                fontWeight: 600,
                fontFamily: 'var(--dp-font)',
                boxShadow: '0 2px 12px rgba(13, 174, 255, 0.25)',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.92'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              <Zap size={15} style={{ flexShrink: 0 }} />
              {showExpanded && t('sidebar.upgrade')}
            </a>
          </div>
        )}

        {/* ═══════════ NAV — Collapsible Groups ═══════════ */}
        <nav style={{
          flex: 1,
          padding: '8px 8px',
          overflowY: 'auto',
          overflowX: 'hidden',
        }}>
          {sidebarGroups.map((group) => (
            <SidebarGroup
              key={group.id}
              group={group}
              isExpanded={showExpanded}
              location={location}
            >
              {group.items.map((item) => renderNavItem(item, group.color))}

              {/* Dynamic channels for "MEUS CANAIS" group */}
              {group.hasDynamicChannels && renderDynamicChannels(group.color)}
              {/* Shared channels section */}
              {group.hasDynamicChannels && renderSharedChannels()}
            </SidebarGroup>
          ))}
        </nav>

        {/* Rodapé de ícones REMOVIDO — privacidade/tema/sessão agora vivem no
            Header (botões redondos + menu do avatar); Configurações fica no
            item "Meu Perfil" do menu. */}
      </aside>
    </>
  );
};

export default Sidebar;
